import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { IMAGES } from "../lib/brand";

export default function Login() {
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const dest = loc.state?.from || "/account";
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = "Member Login · Il Salotto Digitale"; }, []);
  useEffect(() => { if (user) nav(dest, { replace: true }); }, [user]); // eslint-disable-line

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + (dest.startsWith("/") ? dest : "/account");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back.");
      } else {
        await register(form.name, form.email, form.password);
        toast.success("Your account is ready.");
      }
      nav(dest, { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen pt-20 grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden">
        <img src={IMAGES.interior} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/30" />
        <div className="absolute bottom-12 left-12 right-12 text-bg">
          <p className="text-[11px] uppercase tracking-ultra mb-4 text-bg/70">Il Salotto Digitale</p>
          <p className="font-serif text-4xl font-light leading-tight">Your courses, resources and progress — in one considered place.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16 lg:py-0">
        <div className="w-full max-w-md">
          <p className="text-[11px] uppercase tracking-ultra text-accent">Member Area</p>
          <h1 className="mt-4 font-serif font-light text-4xl lg:text-5xl text-ink tracking-tight">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="mt-4 text-sm text-ink-soft font-light">
            {mode === "login" ? "Sign in to access your purchased courses and materials." : "Register to enrol, purchase and access your course library."}
          </p>

          <button type="button" onClick={googleSignIn} data-testid="google-signin-button"
            className="mt-10 w-full border border-ink py-4 text-[11px] uppercase tracking-[0.25em] text-ink hover:bg-ink hover:text-bg transition-colors flex items-center justify-center gap-3">
            <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="currentColor" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34.1 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.2-.1-2.4-.4-3.5z" />
            </svg>
            Continue with Google
          </button>
          <div className="mt-8 flex items-center gap-4" aria-hidden="true">
            <span className="h-px flex-1 bg-line" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-ink-soft">or with email</span>
            <span className="h-px flex-1 bg-line" />
          </div>

          <form onSubmit={submit} className="mt-8 space-y-6" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Name</label>
                <input data-testid="auth-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 w-full bg-transparent border-b border-line focus:border-ink outline-none py-3 text-ink text-lg font-light transition-colors" placeholder="Your full name" />
              </div>
            )}
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Email</label>
              <input data-testid="auth-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 w-full bg-transparent border-b border-line focus:border-ink outline-none py-3 text-ink text-lg font-light transition-colors" placeholder="you@email.com" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Password</label>
              <input data-testid="auth-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-2 w-full bg-transparent border-b border-line focus:border-ink outline-none py-3 text-ink text-lg font-light transition-colors" placeholder="••••••••" />
              {mode === "login" && (
                <div className="mt-3 text-right">
                  <Link to="/forgot-password" data-testid="login-forgot-password-link" className="text-[11px] uppercase tracking-[0.2em] text-ink-soft link-underline">Forgot password?</Link>
                </div>
              )}
            </div>
            <button type="submit" disabled={loading} data-testid="auth-submit"
              className="group w-full bg-ink text-bg py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-[#33312E] transition-colors flex items-center justify-center gap-3 disabled:opacity-60">
              {loading ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
              <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
            </button>
            {mode === "register" && (
              <p className="text-xs font-light text-ink-soft leading-relaxed" data-testid="auth-legal-note">
                By creating an account, you agree to our{" "}
                <Link to="/terms" className="link-underline text-ink">Terms of Service</Link> and{" "}
                <Link to="/privacy" className="link-underline text-ink">Privacy Policy</Link>.
              </p>
            )}
          </form>

          <div className="mt-8 text-sm text-ink-soft">
            {mode === "login" ? (
              <button data-testid="switch-register" onClick={() => setMode("register")} className="link-underline">New here? Create an account</button>
            ) : (
              <button data-testid="switch-login" onClick={() => setMode("login")} className="link-underline">Already have an account? Sign in</button>
            )}
          </div>
          <Link to="/salotto-digitale" className="mt-6 inline-block text-[11px] uppercase tracking-[0.2em] text-ink-soft link-underline">← Back to Il Salotto Digitale</Link>
        </div>
      </div>
    </div>
  );
}
